import { Center, FormControl, FormErrorMessage, FormLabel, Input, Stack } from '@chakra-ui/react'
import React, { FC, useCallback, useState } from 'react'
import { SubmitErrorHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTitle } from 'react-use'
import { useLocation } from 'wouter'
import { apiConfig } from '../../../common/apiConfig'
import { TextLink } from '../../../common/components/TextLink'
import { FormControlWrapper } from '../../../common/form/FormInput'
import { ApplicantRegistrationsApi } from '../../../generated'
import { Button } from '../Button'
import { Title } from '../Title'

interface FormType {
  email: string
}

export const RegistrationRequestForm: FC = () => {
  const [sending, setSending] = useState(false)
  const [t] = useTranslation()
  useTitle(t('SEPAS 新規会員登録'))

  const { control, handleSubmit, setError } = useForm<FormType>({
    mode: 'onBlur',
  })
  const [, setLocation] = useLocation()

  const signUp = useCallback(
    (form) => {
      setSending(true)
      new ApplicantRegistrationsApi(apiConfig)
        .create({ email: form.email })
        .then(() => {
          setLocation('/registrations-request-complete')
        })
        .catch((error) => {
          setSending(false)
          switch (error.status) {
            case 409:
              setError('email', {
                type: 'manual',
                message: `登録済メールアドレスです。`,
              })
              break
          }
        })
    },
    [setError, setLocation, setSending]
  )

  const onError: SubmitErrorHandler<FormType> = useCallback((error, event) => {
    console.info('submit error', error, event)
  }, [])

  return (
    <div>
      <Title text={t('新規会員登録')} />

      <div className={'mt24'}>
        {t('入力したメールアドレスにパスワード登録するためのURLが送信されます。')}
        <br />
        <span className={'fw700 clrRed'}>
          {t(
            '※ 長期間有効なアドレスを登録ください。もしも数年後再申請される場合、同じメールアドレスが必要になります。'
          )}
          <br />
          {t(
            '※ 一度SEPASでアカウントを作成した方は、新しく別のアカウントを作成することはできません。最初に作成したアカウントをご利用ください。'
          )}
        </span>
      </div>

      <div className={'mt24'}>
        <FormControlWrapper<FormType>
          control={control}
          path={'email'}
          skipNormalize={true}
          options={{
            required: '必須入力です。',
            pattern: {
              value:
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
              message: 'メールアドレスを入力してください。',
            },
          }}
        >
          {({ id, isInvalid, error, ...inputProps }) => (
            <FormControl
              id={id}
              isRequired={true}
              isInvalid={isInvalid ? true : undefined}
              as={'fieldset'}
              display={'flex'}
              flexDir={'row'}
              alignItems={`start`}
            >
              <FormLabel w={'10rem'} fontSize={`14px`} fontWeight={700}>
                {t('メールアドレス')}
              </FormLabel>
              <Stack>
                <Input
                  type="email"
                  size={'sm'}
                  w={'24rem'}
                  autoComplete={'email'}
                  {...inputProps}
                  maxLength={200}
                />
                {error?.message && <FormErrorMessage>{t(error.message)}</FormErrorMessage>}
              </Stack>
            </FormControl>
          )}
        </FormControlWrapper>

        <Center className={'mt16'}>
          <Button
            role={'primary'}
            size={'md'}
            text={t('登録')}
            w={'200px'}
            onClick={handleSubmit(signUp, onError)}
            disabled={sending}
          />
        </Center>

        <Center className={'mt8'}>
          <TextLink text={t('キャンセル')} to={'/'} />
        </Center>
      </div>
    </div>
  )
}
